package com.patronat.top.client.presentation.home

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import com.patronat.top.client.deeplink.ClientDeepLinks
import com.patronat.top.client.platform.ClientBackHandler
import com.patronat.top.library.media.data.KmpFileSource
import io.github.vinceglb.filekit.FileKit
import io.github.vinceglb.filekit.dialogs.FileKitMode
import io.github.vinceglb.filekit.dialogs.FileKitType
import io.github.vinceglb.filekit.dialogs.openFilePicker
import io.github.vinceglb.filekit.mimeType
import io.github.vinceglb.filekit.name
import io.github.vinceglb.filekit.readBytes
import org.koin.compose.koinInject

@Composable
fun ClientHomeRoute(
    webMode: Boolean = false,
    onFullScreenChanged: (Boolean) -> Unit = {}
) {
    val viewModel: ClientHomeViewModel = koinInject()
    val state by viewModel.viewState.collectAsState()

    LaunchedEffect(state.currentScreen) {
        onFullScreenChanged(
            state.currentScreen == ClientHomeScreen.VenueDetails ||
                state.currentScreen == ClientHomeScreen.VenueServices ||
                state.currentScreen == ClientHomeScreen.VenueReviews ||
                state.currentScreen == ClientHomeScreen.VenueReview
        )
    }

    LaunchedEffect(viewModel) {
        viewModel.effect.collect { effect ->
            when (effect) {
                ClientHomeEffect.OpenReviewPhotoPicker -> pickReviewPhotos(viewModel)
            }
        }
    }

    LaunchedEffect(viewModel) {
        ClientDeepLinks.observeVenueIds().collect { venueId ->
            viewModel.onEvent(ClientHomeEvent.OnVenueDeepLinkOpened(venueId))
        }
    }

    ClientBackHandler(
        enabled = !webMode && (state.selectedReviewPhotoUrl != null || state.currentScreen != ClientHomeScreen.Feed),
        onBack = {
            when {
                state.selectedReviewPhotoUrl != null -> {
                    viewModel.onEvent(ClientHomeEvent.OnVenueReviewPhotoDismissed)
                }
                state.currentScreen == ClientHomeScreen.LocationSearch -> {
                    viewModel.onEvent(ClientHomeEvent.OnCloseLocationSearchClicked)
                }
                state.currentScreen == ClientHomeScreen.Filters -> {
                    viewModel.onEvent(ClientHomeEvent.OnCloseFiltersClicked)
                }
                state.currentScreen == ClientHomeScreen.AllCategories -> {
                    viewModel.onEvent(ClientHomeEvent.OnBackToHomeClicked)
                }
                state.currentScreen == ClientHomeScreen.VenueDetails -> {
                    viewModel.onEvent(ClientHomeEvent.OnVenueDetailsBackClicked)
                }
                state.currentScreen == ClientHomeScreen.VenueServices -> {
                    viewModel.onEvent(ClientHomeEvent.OnVenueCatalogBackClicked)
                }
                state.currentScreen == ClientHomeScreen.VenueReviews -> {
                    viewModel.onEvent(ClientHomeEvent.OnVenueReviewsBackClicked)
                }
                state.currentScreen == ClientHomeScreen.VenueReview -> {
                    viewModel.onEvent(ClientHomeEvent.OnVenueReviewBackClicked)
                }
                else -> Unit
            }
        }
    )

    ClientHomeContent(
        state = state,
        onEvent = viewModel::onEvent,
        webMode = webMode
    )
}

private suspend fun pickReviewPhotos(viewModel: ClientHomeViewModel) {
    val remainingCount = MaxReviewPhotos - viewModel.viewState.value.reviewForm.photos.size
    if (remainingCount <= 0) return

    val files = FileKit.openFilePicker(
        type = FileKitType.Image,
        mode = FileKitMode.Multiple(maxItems = remainingCount)
    ).orEmpty()

    files.forEach { file ->
        val fileName = file.name.ifBlank { "review-photo.jpg" }
        val bytes = runCatching { file.readBytes() }.getOrNull() ?: return@forEach
        val mimeType = file.mimeType()?.toString()
            ?.takeIf { it.startsWith("image/") }
            ?: fileName.defaultImageMimeType()

        viewModel.onEvent(
            ClientHomeEvent.OnReviewPhotoPicked(
                ClientPickedFile(
                    fileSource = KmpFileSource(
                        name = fileName,
                        bytes = bytes,
                        platformSource = file
                    ),
                    mimeType = mimeType,
                    fileName = fileName
                )
            )
        )
    }
}

private fun String.defaultImageMimeType(): String {
    return when (substringAfterLast('.', missingDelimiterValue = "").lowercase()) {
        "png" -> "image/png"
        "webp" -> "image/webp"
        "heic" -> "image/heic"
        "heif" -> "image/heif"
        else -> "image/jpeg"
    }
}

private const val MaxReviewPhotos = 5
